/**
 * @file useBoardDeepLink.ts
 * @description Applies a `?boardId=<uuid>` deep-link param to the active
 *   player-kanban board.
 * @module features/player-kanban/hooks/useBoardDeepLink
 */

import { useEffect, useRef, useState } from "react";

import { useLocalSearchParams } from "expo-router";

import { readInitialRouteParam } from "@/utils/persistedRouteParams";

import {
  BOARD_ID_PARAM,
  PLAYER_KANBAN_ROUTE_KEY,
} from "../constants/routeParams";

/**
 * Arguments for {@link useBoardDeepLink}.
 */
interface UseBoardDeepLinkArgs {
  /**
   * Boards available for the active source type. Used only to gate the
   * deep-link until at least one board has loaded (the async board list is
   * empty on first render); switchBoard itself performs the id match.
   */
  boards: readonly { id: string }[];
  /**
   * True once the board list query has settled. The param is only applied
   * after boards load, so a `boardId` present on the very first (loading)
   * render is not dropped — it is applied as soon as data is ready.
   */
  boardsLoaded: boolean;
  /**
   * True while the viewer's board set can still CHANGE — the connection walk
   * is still fetching pages (`useBoardManager`'s `isResolvingBoardSet`), or
   * the role/flag inputs that pick the source type have not settled yet
   * (`useIsPipelineManager` fails closed while `/auth/me` is in flight, so the
   * surface transiently resolves Watchlist before flipping to Pipeline).
   *
   * SE-7120: this is the difference between "not here YET" and "not here".
   * A miss while this holds is provisional and must be retried, never latched.
   */
  boardSetPending: boolean;
  /**
   * Activates the board with the given id and reports whether it matched.
   * `false` means nothing changed — the target is not in the currently
   * resolved board list.
   */
  switchBoard: (boardId: string) => boolean;
  /**
   * Called once with the deep-linked id when it is CONCLUSIVELY unresolvable
   * (the board set has settled and still does not contain it). The consumer
   * must tell the user; leaving them on an unrelated board with no feedback is
   * the SE-7120 defect itself.
   */
  onUnresolved: (boardId: string) => void;
}

/**
 * Drives `switchBoard` from the `boardId` URL query param so a big-board
 * update tapped in Home > Updates lands on the *corresponding* board
 * (SE-4994), not the default/persisted one — and so a page reload lands back
 * on the pipeline the user was already on (SE-6916).
 *
 * @param args - Boards, load state, and the switchBoard action.
 * @param args.boards - Boards available for the active source type.
 * @param args.boardsLoaded - True once the board list query has settled.
 * @param args.boardSetPending - True while the board set can still change.
 * @param args.switchBoard - Activates the board with the given id.
 * @param args.onUnresolved - Reports a conclusively unresolvable deep link.
 * @returns Nothing; the effect activates the deep-linked board as a side
 *   effect.
 * @remarks
 * - Two consumers, one mechanism. SE-4994 supplies `boardId` as a live URL
 *   param (Home > Updates). SE-6916 supplies it as a seed recovered by
 *   {@link readInitialRouteParam} after a hard reload, written by
 *   `useBoardSelectionPersistence`. The live param wins when
 *   both exist: it is the newer, explicit intent.
 * - The `readInitialRouteParam` layer is not optional for reload survival.
 *   Expo Router on web canonicalizes the URL during route hydration and drops
 *   unrecognized query params via `history.replaceState` BEFORE React renders,
 *   so `useLocalSearchParams` alone reads `undefined` on the very reload this
 *   is meant to survive. The seed is captured once (module-load URL snapshot,
 *   then the sessionStorage mirror) and held in state so the value stays
 *   stable across re-renders instead of being re-read from storage each time.
 * - Reacts only when the effective `boardId` *changes* (tracked via a ref),
 *   never on unrelated re-renders, so it does not stomp the user's manual
 *   in-page board switching. The param is re-applied if a new `boardId`
 *   arrives (e.g. tapping a second big-board update).
 * - Waits for `boardsLoaded` because the board list is async; a `boardId`
 *   present before boards load is applied once they are ready.
 * - A miss is provisional until the board set settles (SE-7120). The board
 *   list arrives in PHASES: `useIsPipelineManager` fails closed while
 *   `/auth/me` is in flight, so the surface first resolves Watchlist/Roster
 *   and only flips to Pipeline once the role lands — and `useBoardManager`
 *   walks the connection a page at a time. The param is therefore held pending
 *   across a miss and re-tried as the set grows; latching on the FIRST
 *   evaluation is what stranded a pipeline deep link on `filteredBoards[0]` —
 *   a real, populated, wrong board, with nothing about the player the user
 *   clicked. (Same latch defect `usePlayerDeepLink` hit against lazily
 *   hydrating columns; same resolution.)
 * - Only once the set has settled and the id is still absent is the deep link
 *   unresolvable, and then it is REPORTED via `onUnresolved` rather than
 *   swallowed. The default board stays active — that fallback is fine; being
 *   silent about it is not.
 * - The `boardId` param is INBOUND only. Nothing in this feature writes it back
 *   to the URL, and nothing may: this hook reads `useLocalSearchParams`, so a
 *   write would echo into it a render out of phase with the board manager's
 *   restore and alternate between two boards until React gives up (SE-6916
 *   round 2, crashed on staging). See `useBoardSelectionPersistence`.
 */
export function useBoardDeepLink({
  boards,
  boardsLoaded,
  boardSetPending,
  switchBoard,
  onUnresolved,
}: UseBoardDeepLinkArgs): void {
  const { boardId: liveBoardId } = useLocalSearchParams<{ boardId?: string }>();

  // Captured once via the state initializer: the seed must reflect the URL as
  // it was on arrival, and the persistence hook rewrites the storage mirror
  // moments later, so re-reading on every render would observe our own write
  // rather than the user's pre-reload intent.
  const [seedBoardId] = useState<string | undefined>(() =>
    readInitialRouteParam(PLAYER_KANBAN_ROUTE_KEY, BOARD_ID_PARAM)
  );

  const boardId = liveBoardId ?? seedBoardId;

  // Track the last param value we acted on so we only react to genuine
  // changes — never re-applying on an unrelated re-render (which would fight
  // a manual board switch).
  const appliedBoardIdRef = useRef<string | undefined>(undefined);

  const hasBoards = boards.length > 0;

  useEffect(() => {
    if (!boardsLoaded || !hasBoards) {
      return;
    }
    if (!boardId) {
      return;
    }
    if (appliedBoardIdRef.current === boardId) {
      return;
    }

    // The board set arrives in phases, so ask whether the target is reachable
    // NOW rather than assuming this evaluation is the last one.
    if (switchBoard(boardId)) {
      appliedBoardIdRef.current = boardId;
      return;
    }

    // A miss while the set can still grow is "not yet", not "not there".
    // Staying pending is what lets the Pipeline board list — which only loads
    // after the Manager+ role resolves — still win the deep link. `switchBoard`
    // no-ops on a miss, so re-trying costs a lookup and changes nothing.
    if (boardSetPending) {
      return;
    }

    // Settled and still absent: the link is genuinely unresolvable for this
    // viewer. Latch it (so a board created minutes later cannot yank the user
    // off whatever they navigated to since) and say so.
    appliedBoardIdRef.current = boardId;
    onUnresolved(boardId);
  }, [
    boardId,
    boardSetPending,
    boardsLoaded,
    hasBoards,
    onUnresolved,
    switchBoard,
  ]);
}
